import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
} from '@mui/material';
import {
  CheckCircle as HealthyIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';

const SystemMonitor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusRes, metricsRes] = await Promise.all([
        axios.get('/api/monitoring/status'),
        axios.get('/api/monitoring/metrics'),
      ]);

      setStatus(statusRes.data);
      setMetrics(metricsRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      setError(error.response?.data?.error || 'Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    return status === 'healthy' ? 'success' : 'error';
  };

  const getMemoryPercentage = () => {
    if (!status?.system?.memory) return 0;
    return ((status.system.memory.used / status.system.memory.total) * 100).toFixed(1);
  };

  if (loading && !status) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          System Health Monitor
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/admin')}
          >
            Back to Admin
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Overall Status */}
      {status && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            {status.status === 'healthy' ? (
              <HealthyIcon sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
            )}
            <Box>
              <Typography variant="h5" fontWeight="bold">
                System Status: <Chip label={status.status.toUpperCase()} color={getStatusColor(status.status)} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Environment: {status.environment?.nodeEnv} | Version: {status.version}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Metrics Cards */}
      {status && (
        <Grid container spacing={3} mb={3}>
          {/* Database Health */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6">Database</Typography>
                </Box>
                <Chip
                  label={status.database.status}
                  size="small"
                  color={status.database.status === 'connected' ? 'success' : 'error'}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Response Time: {status.database.responseTime}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Users: {status.database.userCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pool: {status.database.pool.total} total, {status.database.pool.idle} idle, {status.database.pool.waiting} waiting
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Memory Usage */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <MemoryIcon color="primary" />
                  <Typography variant="h6">Memory</Typography>
                </Box>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  {getMemoryPercentage()}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(getMemoryPercentage())}
                  sx={{ my: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {status.system.memory.used} MB / {status.system.memory.total} MB
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Process: {status.system.memory.processUsed} MB
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Performance */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <SpeedIcon color="primary" />
                  <Typography variant="h6">Performance</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Response Time: {status.responseTime}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Uptime: {Math.floor(status.system.uptime / 3600)}h {Math.floor((status.system.uptime % 3600) / 60)}m
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CPUs: {status.system.cpu.count} cores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Load: [{status.system.cpu.loadAverage.map(l => l.toFixed(2)).join(', ')}]
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Application Metrics */}
      {metrics && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Application Metrics
          </Typography>

          <Grid container spacing={3}>
            {/* Projects */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Projects
                </Typography>
                {Object.entries(metrics.projects.byStatus).map(([status, count]) => (
                  <Box key={status} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">{status}:</Typography>
                    <Typography variant="body2" fontWeight="bold">{count}</Typography>
                  </Box>
                ))}
                <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop={1} borderColor="divider">
                  <Typography variant="body2" fontWeight="bold">Active:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {metrics.projects.active}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Users */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Users
                </Typography>
                {Object.entries(metrics.users.byRole).map(([role, count]) => (
                  <Box key={role} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">{role}:</Typography>
                    <Typography variant="body2" fontWeight="bold">{count}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Estimates */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estimates
                </Typography>
                {Object.entries(metrics.estimates.byStatus).map(([status, count]) => (
                  <Box key={status} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">{status}:</Typography>
                    <Typography variant="body2" fontWeight="bold">{count}</Typography>
                  </Box>
                ))}
                <Box display="flex" justifyContent="space-between" mt={1} pt={1} borderTop={1} borderColor="divider">
                  <Typography variant="body2" fontWeight="bold" color="error">Disputes:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="error">
                    {metrics.disputes.active}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* System Info */}
      {status && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell><strong>Platform:</strong></TableCell>
                  <TableCell>{status.system.platform} ({status.system.arch})</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Node Version:</strong></TableCell>
                  <TableCell>{status.system.nodeVersion}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Environment:</strong></TableCell>
                  <TableCell>{status.environment.nodeEnv}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Port:</strong></TableCell>
                  <TableCell>{status.environment.port}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Total Memory:</strong></TableCell>
                  <TableCell>{status.system.memory.total} MB</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Free Memory:</strong></TableCell>
                  <TableCell>{status.system.memory.free} MB</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Container>
  );
};

export default SystemMonitor;
